import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircleIcon,
  XMarkIcon,
  GlobeAltIcon,
  DocumentArrowUpIcon,
  CpuChipIcon,
  RocketLaunchIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { platformApi } from '../services/api'

const ONBOARDING_KEY = 'vulnzero_onboarding_dismissed'

const steps = [
  {
    id: 'welcome',
    title: 'Welcome to VulnZero',
    description: 'Your autonomous vulnerability remediation platform. Let\'s get you set up in 5 minutes.',
    icon: RocketLaunchIcon,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
  },
  {
    id: 'scanner',
    title: 'Connect a Scanner',
    description: 'Connect OpenVAS or upload scan results. Go to Network Scanner and test the connection.',
    icon: GlobeAltIcon,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    route: '/network-scanner',
    action: 'Go to Scanner',
  },
  {
    id: 'upload',
    title: 'Import Vulnerabilities',
    description: 'Upload a CSV, Excel, or JSON file from any scanner (Nessus, Qualys, OUTSCAN, etc.).',
    icon: DocumentArrowUpIcon,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    route: '/scan',
    action: 'Upload Scan Results',
  },
  {
    id: 'pipeline',
    title: 'Run the Pipeline',
    description: 'VulnZero will analyze, generate patches, test in a digital twin, and deploy fixes autonomously.',
    icon: CpuChipIcon,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    action: 'Run Pipeline (from Dashboard)',
  },
  {
    id: 'review',
    title: 'Review Results',
    description: 'Check the Tickets page for remediation status, approve critical fixes, and monitor compliance.',
    icon: ChartBarIcon,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    route: '/tickets',
    action: 'View Tickets',
  },
]

export default function OnboardingWizard() {
  const [visible, setVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState({})
  const navigate = useNavigate()

  // Check if onboarding should show using the real onboarding status API
  const checkOnboarding = useCallback(async () => {
    if (localStorage.getItem(ONBOARDING_KEY) === 'true') return

    try {
      const res = await platformApi.onboardingStatus()
      const status = res.data

      // Map API steps to wizard steps
      const completed = { welcome: true }
      if (status.steps?.scanner_connected?.done) completed.scanner = true
      if (status.steps?.vulnerabilities_imported?.done) completed.upload = true
      if (status.steps?.pipeline_executed?.done) completed.pipeline = true
      if (status.steps?.patches_generated?.done) completed.review = true

      setCompletedSteps(completed)

      // If everything is done, auto-dismiss
      if (status.progress_percentage === 100) {
        localStorage.setItem(ONBOARDING_KEY, 'true')
        return
      }

      setVisible(true)
    } catch {
      // Can't load status -- show wizard anyway
      setVisible(true)
      setCompletedSteps({ welcome: true })
    }
  }, [])

  useEffect(() => {
    checkOnboarding()
  }, [checkOnboarding])

  const handleDismiss = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setVisible(false)
  }

  const handleStepAction = (step) => {
    if (step.route) {
      navigate(step.route)
    }
    setCompletedSteps((prev) => ({ ...prev, [step.id]: true }))
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const completedCount = Object.values(completedSteps).filter(Boolean).length
  const progressPercent = Math.round((completedCount / steps.length) * 100)

  if (!visible) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="glass-card rounded-xl border border-cyan-500/20 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RocketLaunchIcon className="h-5 w-5 text-cyan-400" />
            <div>
              <h2 className="text-sm font-semibold text-white">Getting Started</h2>
              <p className="text-xs text-slate-500">{completedCount}/{steps.length} steps complete</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Progress bar */}
            <div className="w-24 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
              />
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
              title="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Steps */}
        <div className="px-6 py-4">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isComplete = completedSteps[step.id]
              const isCurrent = index === currentStep

              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(index)}
                  className={`flex-shrink-0 flex items-center gap-2.5 px-4 py-3 rounded-lg border transition-all text-left min-w-[180px] ${
                    isCurrent
                      ? `${step.border} ${step.bg}`
                      : isComplete
                      ? 'border-emerald-500/20 bg-emerald-500/5'
                      : 'border-slate-700/30 bg-slate-900/30 hover:border-slate-600/50'
                  }`}
                >
                  {isComplete ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-400 shrink-0" />
                  ) : (
                    <Icon className={`h-5 w-5 shrink-0 ${isCurrent ? step.color : 'text-slate-500'}`} />
                  )}
                  <div>
                    <p className={`text-xs font-medium ${isComplete ? 'text-emerald-400' : isCurrent ? 'text-white' : 'text-slate-400'}`}>
                      {step.title}
                    </p>
                    {isCurrent && (
                      <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{step.description}</p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Current step detail */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15 }}
              className="mt-4 flex items-center justify-between"
            >
              <p className="text-sm text-slate-400 max-w-lg">
                {steps[currentStep].description}
              </p>
              {steps[currentStep].action && (
                <button
                  onClick={() => handleStepAction(steps[currentStep])}
                  className={`shrink-0 ml-4 px-4 py-2 rounded-lg text-xs font-medium border transition-all ${steps[currentStep].border} ${steps[currentStep].bg} ${steps[currentStep].color} hover:opacity-80`}
                >
                  {steps[currentStep].action}
                </button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
